/**
 * Helpers for safely handling a user-supplied `returnTo` redirect target.
 *
 * `returnTo` flows in from query params / request bodies, so it is fully
 * attacker-controllable. Anywhere it ends up as a redirect destination we must
 * confirm it is a same-origin path, otherwise it becomes an open redirect
 * (e.g. `https://evil.com`, the scheme-relative `//evil.com`, or the
 * backslash-tricked `/\evil.com`, all of which browsers resolve off-origin).
 */

/**
 * Return `value` only if it is a safe, same-origin absolute path; otherwise
 * undefined. A safe value has exactly one leading slash and is neither
 * scheme-relative (`//host`) nor backslash-tricked (`/\host`).
 */
export const getSafeReturnTo = (
  value: string | string[] | undefined,
): string | undefined => {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined
  }

  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.startsWith('/\\')
  ) {
    return undefined
  }

  return value
}

/**
 * Strip the app basePath from a returnTo, but only when it is the whole leading
 * segment. Next re-applies basePath to redirect destinations, so a returnTo
 * that already includes it would otherwise double up (/executive/executive/…).
 * Matching `${basePath}` exactly or `${basePath}/…` avoids clobbering unrelated
 * prefixes such as `/executive-foo`.
 */
export const stripReturnToBasePath = (
  value: string,
  basePath: string,
): string => {
  if (!basePath || basePath === '/') {
    return value
  }
  if (value === basePath) {
    return '/'
  }
  if (value.startsWith(`${basePath}/`)) {
    return value.slice(basePath.length)
  }
  return value
}

/**
 * Resolve a user-supplied returnTo into a meaningful redirect destination:
 * a safe, same-origin path with the app basePath stripped, or undefined when it
 * is absent, unsafe, or points at the bare app root (`/`) — which carries no
 * destination beyond the default. Both the login and post-login redirects call
 * this so the same returnTo can't resolve to two different pages.
 */
export const resolveReturnTo = (
  value: string | string[] | undefined,
  basePath: string,
): string | undefined => {
  const safe = getSafeReturnTo(value)
  if (!safe) {
    return undefined
  }
  // Re-validate after stripping: removing the basePath can re-expose an
  // off-origin value that the first check accepted. e.g. `/executive//evil.com`
  // passes getSafeReturnTo (one leading slash), but strips to `//evil.com`
  // (scheme-relative) — and `/executive/\evil.com` strips to `/\evil.com`.
  // Both would reach the redirect sink unguarded without this second pass.
  const stripped = getSafeReturnTo(stripReturnToBasePath(safe, basePath))
  return !stripped || stripped === '/' ? undefined : stripped
}
